import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import roshan from "../assets/images/team/roshan.jpg";
import arun from "../assets/images/team/arun.jpg";
import akash from "../assets/images/team/akash.jpg";
import jisna from "../assets/images/team/jisna.jpg";
import arjun from "../assets/images/team/arjun.jpg";

// --- FUTURISTIC ANIMATED BACKGROUND COMPONENT (Blue & Silver Theme) ---
function FuturisticBackground() {
  const { scrollY } = useScroll();

  const yGrid = useTransform(scrollY, [0, 1000], [0, -150]);
  const rotateGrid = useTransform(scrollY, [0, 1000], [0, 5]);
  const yOrb2 = useTransform(scrollY, [0, 1000], [0, -200]);
  const rotateRing = useTransform(scrollY, [0, 2000], [0, 360]);
  const smoothRotate = useSpring(rotateRing, { stiffness: 50, damping: 20 });

  return (
    <div className="fixed inset-0 overflow-hidden -z-10 bg-[#003049]">
      {/* Deep Blue Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#001824] via-[#003049] to-[#004e75]" />
      
      {/* Grid */}
      <motion.div 
        style={{ y: yGrid, rotateX: rotateGrid }}
        className="absolute inset-[-50%] w-[200%] h-[200%] origin-top bg-[linear-gradient(to_right,#457B9D22_1px,transparent_1px),linear-gradient(to_bottom,#457B9D22_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] perspective-1000" 
      />
      
      {/* Steel Blue Orb */}
      <motion.div 
        style={{ y: yOrb2 }}
        className="absolute bottom-[10%] right-[5%] w-[40vw] h-[40vw] bg-[#457B9D] rounded-full blur-[140px] opacity-30 mix-blend-screen"
      />
      
      {/* Silver Center Ring */}
      <motion.div
        style={{ rotate: smoothRotate }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#D1D1D1]/10 rounded-full border-dashed pointer-events-none"
      />
      
      {/* Silver Particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#D1D1D1] rounded-full"
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            scale: Math.random() * 2,
            opacity: Math.random() * 0.5 + 0.3
          }}
          animate={{
            y: [null, Math.random() * -150],
            opacity: [null, 0.1, 0.6, 0.1],
          }}
          transition={{
            duration: Math.random() * 15 + 15,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-[#457B9D]/40 to-transparent blur-[80px] pointer-events-none" />
    </div>
  );
}

function AboutUs() {
  const team = [
    {
      name: "Roshan Siraj",
      role: "Chief Executive Officer & Co-Founder",
      img: roshan,
      alt: "roshan",
      social: { linkedin: "https://www.linkedin.com/in/roshan-siraj/" }
    },
    {
      name: "Arun K",
      role: "Chief Technology Officer & Co-Founder",
      img: arun,
      alt: "arun",
      social: { linkedin: "https://www.linkedin.com/in/arunkarooth/", github: "https://github.com/the-photographr" }
    },
    {
      name: "Akash M",
      role: "Chief Management Officer & Co-Founder",
      img: akash,
      alt: "akash",
      social: { linkedin: "https://www.linkedin.com/in/akashmadhu/" }
    },
    {
        name: "Arjun Anoop",
        role: "Human Resourse Manager",
        img: arjun,
        alt: "arjun",
        social: { linkedin: "https://linkedin.com/in/roshan" }
    },
    // {
    //   name: "Jisna Raj P P",
    //   role: " Data Analyst",
    //   img: jisna,
    //   alt: "jisna",
    //   social: { linkedin: "https://www.linkedin.com/in/jisna-raj-p-p-42716a252/", github: "https://github.com/Jisnaraj" }
    // },
    // {
    //   name: "Abidh Mammi K",
    //   role: " International Development Officer",
    //   img: "https://images.unsplash.com/photo-1511367461989-f85a21fda167?auto=format&fit=facearea&w=256&h=256&facepad=2",
    //   alt: "abidh",
    //   social: { linkedin: "https://linkedin.com/in/roshan" }
    // },
  ];

  const mission = [
    {
      icon: "🏢",
      title: "Innovative Robotics Solutions",
      desc: "We deliver advanced robotics products that drive progress in education and industry.",
    },
    {
      icon: "🎓",
      title: "Comprehensive Training",
      desc: "Our courses are designed to equip learners with practical skills for the future workforce.",
    },
    {
      icon: "🌐",
      title: "Global Impact",
      desc: "We aim to make robotics education accessible worldwide, fostering a global community of creators.",
    },
    {
      icon: "👥",
      title: "Expert Team",
      desc: "Our team consists of leading experts passionate about robotics and education.",
    },
    {
      icon: "💡",
      title: "Continuous Innovation",
      desc: "We constantly evolve our offerings to stay ahead in the fast-changing world of robotics.",
    },
    {
      icon: "🤝",
      title: "Trusted Partnerships",
      desc: "We collaborate with industry leaders and academic institutions to maximize impact.",
    },
  ];

  return (
    <div className="font-sans min-h-screen py-12 px-4 pt-24 text-gray-200 selection:bg-[#D1D1D1] selection:text-[#003049] relative overflow-hidden">
      <FuturisticBackground />

      {/* About Section */}
      <motion.section 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mb-16 relative z-10"
      >
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-8 text-white drop-shadow-md">
          About <span className="text-[#D1D1D1]">Us</span>
        </h2>
        
        <div className="bg-[#002639]/60 backdrop-blur-md border border-[#457B9D] p-8 rounded-2xl shadow-xl">
          <p className="text-center text-gray-300 mb-6 leading-relaxed">
            <span className="text-[#D1D1D1] font-semibold">Ente AI</span> is a next-generation technology and research startup based in Kerala, India, dedicated to making cutting-edge innovations accessible, practical, and impactful. We empower students, innovators, and professionals to learn, build, and grow with AI, IoT, Robotics, Embedded Systems, Drones, 3D Printing, and much more.
          </p>
          <p className="text-center text-gray-300 mb-6 leading-relaxed">
            At Ente AI, we believe education should go beyond theory. That’s why our programs are designed around a project-first approach, where every learner works on real-world solutions — from autonomous rovers and drone systems to AI-driven applications and smart IoT devices.
          </p>
          <p className="text-center text-gray-300 mb-6 leading-relaxed">
            We are more than just a training center — we are a hub of innovation. Our ecosystem blends learning, research, and startup culture, giving students exposure to industry tools, hands-on kits, and collaboration opportunities that prepare them for careers, entrepreneurship, and global challenges.
          </p>
          <p className="text-center text-gray-300 leading-relaxed">
            Through our initiatives like Student Ambassador Programs, Project Assistance, and Industry Collaborations, we aim to build a strong community of creators who don’t just adapt to the future — but shape it.
          </p>
        </div>
      </motion.section>

      {/* Mission Section */}
      <section className="max-w-6xl mx-auto mb-16 relative z-10">
        <motion.div
           initial={{ opacity: 0, y: 20 }}
           whileInView={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6 }}
           viewport={{ once: true }}
           className="text-center mb-10"
        >
          <h1 className="text-4xl font-bold mb-4 text-white">Our <span className="text-[#D1D1D1]">Mission</span></h1>
          <p className="text-gray-300 max-w-3xl mx-auto">
            We empower the next generation of innovators by making AI, Robotics, IoT, and Emerging Technologies accessible through hands-on learning and real-world projects. We believe talent is everywhere, so we bring advanced tech education to emerging regions—nurturing creativity and problem-solving to shape future-ready leaders.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mission.map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="flex flex-col items-center p-8 bg-[#002639]/80 backdrop-blur-sm border border-[#457B9D] rounded-xl hover:border-[#D1D1D1] hover:shadow-[0_0_15px_rgba(209,209,209,0.15)] transition-all duration-300 group"
            >
              <div className="text-4xl mb-4 text-[#D1D1D1] group-hover:scale-110 transition-transform duration-300">{item.icon}</div>
              <h3 className="font-bold text-lg mb-2 text-white text-center group-hover:text-[#D1D1D1] transition-colors">{item.title}</h3>
              <p className="text-gray-400 text-center text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Vision Section */}
      <motion.section 
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mb-16 relative z-10"
      >
        <div className="bg-gradient-to-r from-[#457B9D]/20 to-[#003049]/40 border border-[#D1D1D1]/30 p-10 rounded-2xl text-center backdrop-blur-md">
          <h2 className="text-3xl font-bold mb-4 text-[#D1D1D1]">Our Vision</h2>
          <p className="text-gray-200 italic text-lg leading-relaxed">
            "At Ente AI, we envision a world where technology education and innovation are not confined to big cities. By empowering young minds in every region with skills and opportunities in AI, Robotics, IoT, and emerging technologies, we aim to transform communities into centers of innovation—building changemakers who shape industries, uplift communities, and redefine the future."
          </p>
        </div>
      </motion.section>

      {/* Team Section */}
      <section className="max-w-6xl mx-auto relative z-5">
        <h2 className="text-3xl font-bold text-center mb-4 text-white">Meet Our <span className="text-[#D1D1D1]">Team</span></h2>
        <p className="text-center text-gray-400 mb-10 max-w-2xl mx-auto">
          Our passionate and experienced team is dedicated to advancing robotics education and technology. Get to know the minds behind Robotics Academy Portal.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-items-center">
          {team.map((member, idx) => (
            <motion.div 
              key={member.name} 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="flex flex-col items-center group w-full"
            >
              <div className="relative mb-4">
                <div className="absolute inset-0 bg-[#D1D1D1] rounded-xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
                <img
                  src={member.img}
                  alt={member.alt}
                  className="relative w-40 h-40 object-cover rounded-xl border-2 border-[#457B9D] group-hover:border-[#D1D1D1] transition-colors duration-300"
                />
              </div>
              
              <div className="font-bold text-lg text-white group-hover:text-[#D1D1D1] transition-colors">{member.name}</div>
              <div className="text-gray-400 text-sm mb-3 text-center h-10">{member.role}</div>
              
              {/* Social Icons */}
              <div className="flex gap-3">
                {member.social?.linkedin && (
                  <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                    <img src="https://img.icons8.com/color/32/linkedin.png" alt="LinkedIn" className="w-6 h-6 grayscale group-hover:grayscale-0 transition-all" />
                  </a>
                )}
                {member.social?.github && (
                  <a href={member.social.github} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                    <img src="https://img.icons8.com/color/32/github.png" alt="GitHub" className="w-6 h-6 grayscale group-hover:grayscale-0 transition-all" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
